<template>
    <div class="redeem-status-backdrop">
        <div class="redeem-status-card" :class="{ 'is-success': status === 'success' }">

            <div v-if="status === 'loading'" class="redeem-loading">
                <div class="redeem-spinner"></div>
                <p class="redeem-message">Redeeming your voucher...</p>
            </div>

            <div v-else-if="status === 'success'" class="redeem-success">
                <div ref="lottieContainer" class="redeem-video"></div>
                <p class="redeem-message">Voucher redeemed!</p>
                <button type="button" class="redeem-close" @click="$emit('close')">
                    Close
                </button>
            </div>

            <div v-else-if="status === 'error'" class="redeem-error">
                <div ref="errorLottieContainer" class="redeem-video"></div>
                <p class="redeem-message">Not enough points to redeem this voucher</p>
                <button type="button" class="redeem-close" @click="$emit('close')">
                    Close
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import lottie from 'lottie-web';

export default {
    name: 'VoucherRedeemStatusModal',
    props: {
        status: {
            type: String,
            default: 'loading',
            validator: value => ['loading', 'success', 'error'].includes(value)
        }
    },
    emits: ['close'],
    data() {
        return {
            videoKey: 0,
            animInstance: null
        };
    },
    mounted() {
        document.body.style.overflow = 'hidden';
        if (this.status === 'success' || this.status === 'error') {
            this.initLottie();
        }
    },
    beforeUnmount() {
        document.body.style.overflow = '';
        this.destroyLottie();
    },
    watch: {
        status(newValue) {
            if (newValue === 'success' || newValue === 'error') {
                this.$nextTick(() => {
                    this.initLottie();
                });
            }
        }
    },
    methods: {
        async initLottie() {
            this.destroyLottie();
            await this.$nextTick();

            const container = this.status === 'success' 
                ? this.$refs.lottieContainer 
                : this.$refs.errorLottieContainer;
            if (!container) return;

            const path = this.status === 'success'
                ? '/video/voucher-reedem-animation.json'
                : '/video/no-poin-animation.json';

            try {
                const response = await fetch(path);
                const animData = await response.json();

                // Read active theme CSS variables from :root
                const rootStyle = getComputedStyle(document.documentElement);
                const primaryHex = (rootStyle.getPropertyValue('--primary-color') || '#F27178').trim();
                const secondaryHex = (rootStyle.getPropertyValue('--secondary-color') || '#FEE9E9').trim();
                const bottomHex = (rootStyle.getPropertyValue('--bottom') || '#663434').trim();

                const hexToRgba = (hex, defaultRgba) => {
                    if (!hex || !hex.startsWith('#')) return defaultRgba;
                    let c = hex.replace('#', '').trim();
                    if (c.length === 3) c = c.split('').map(x => x + x).join('');
                    const num = parseInt(c, 16);
                    if (isNaN(num)) return defaultRgba;
                    return [
                        ((num >> 16) & 255) / 255,
                        ((num >> 8) & 255) / 255,
                        (num & 255) / 255,
                        1
                    ];
                };

                const newPrimary = hexToRgba(primaryHex, [0.949, 0.443, 0.471, 1]);
                const newSecondary = hexToRgba(secondaryHex, [0.996, 0.914, 0.914, 1]);
                const newBottom = hexToRgba(bottomHex, [0.4, 0.204, 0.204, 1]);

                // Base reference colors in the JSON templates
                const OLD_PRIMARY = [0.9490196078431372, 0.4431372549019608, 0.47058823529411764, 1];
                const OLD_SECONDARY = [0.996078431372549, 0.9137254901960784, 0.9137254901960784, 1];
                const OLD_HIGHLIGHT = [1.0, 0.9490196078431372, 0.9529411764705882, 1];
                const OLD_BOTTOM = [0.4, 0.20392156862745098, 0.20392156862745098, 1];

                const closeColor = (c1, c2, tol = 0.08) => {
                    if (!Array.isArray(c1) || !Array.isArray(c2) || c1.length < 3) return false;
                    return Math.abs(c1[0] - c2[0]) < tol &&
                           Math.abs(c1[1] - c2[1]) < tol &&
                           Math.abs(c1[2] - c2[2]) < tol;
                };

                const updateColors = (obj) => {
                    if (obj && typeof obj === 'object') {
                        if (obj.c && Array.isArray(obj.c.k) && obj.c.k.length >= 3) {
                            if (closeColor(obj.c.k, OLD_PRIMARY)) {
                                obj.c.k = newPrimary;
                            } else if (closeColor(obj.c.k, OLD_SECONDARY)) {
                                obj.c.k = newSecondary;
                            } else if (closeColor(obj.c.k, OLD_HIGHLIGHT)) {
                                obj.c.k = [
                                    1 - (1 - newPrimary[0]) * 0.12,
                                    1 - (1 - newPrimary[1]) * 0.12,
                                    1 - (1 - newPrimary[2]) * 0.12,
                                    1
                                ];
                            } else if (closeColor(obj.c.k, OLD_BOTTOM)) {
                                obj.c.k = newBottom;
                            }
                        }
                        for (let k in obj) {
                            if (Object.prototype.hasOwnProperty.call(obj, k)) {
                                updateColors(obj[k]);
                            }
                        }
                    } else if (Array.isArray(obj)) {
                        obj.forEach(item => updateColors(item));
                    }
                };

                updateColors(animData);

                this.animInstance = lottie.loadAnimation({
                    container: container,
                    renderer: 'svg',
                    loop: false,
                    autoplay: true,
                    animationData: animData
                });
            } catch (e) {
                // Fallback to static path loading if fetch fails
                this.animInstance = lottie.loadAnimation({
                    container: container,
                    renderer: 'svg',
                    loop: false,
                    autoplay: true,
                    path: path
                });
            }

            if (this.animInstance) {
                this.animInstance.addEventListener('complete', () => {
                    this.handleVideoEnded();
                });
            }
        },
        destroyLottie() {
            if (this.animInstance) {
                this.animInstance.destroy();
                this.animInstance = null;
            }
        },
        restartVideo() {
            this.initLottie();
        },
        handleVideoEnded() {
            this.$emit('close');
        }
    }
}
</script>

<style scoped>
.redeem-status-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(17, 24, 39, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    z-index: 1200;
}

.redeem-status-card {
    position: relative;
    width: min(360px, 100%);
    background: #ffffff;
    border-radius: 24px;
    padding: 32px 28px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    overflow: hidden;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
    border: 2px solid var(--primary-border, var(--primary-light, rgba(242, 113, 120, 0.15)));
}

.redeem-loading,
.redeem-success {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.redeem-spinner {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    border: 6px solid var(--secondary-color, #FEE9E9);
    border-top-color: var(--primary-color, #F27178);
    animation: redeemSpin 1s linear infinite;
}

.redeem-message {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    text-align: center;
}

.redeem-video {
    width: 180px;
    height: auto;
    border-radius: 18px;
}

/* Dynamic theme styling for Lottie SVG paths */
.redeem-video :deep(svg path[fill="#F27178"]),
.redeem-video :deep(svg path[fill="rgb(242, 113, 120)"]) {
    fill: var(--primary-color, #F27178) !important;
}

.redeem-video :deep(svg path[fill="#FEE9E9"]),
.redeem-video :deep(svg path[fill="rgb(254, 233, 233)"]) {
    fill: var(--secondary-color, #FEE9E9) !important;
}

.redeem-video :deep(svg path[stroke="#663434"]),
.redeem-video :deep(svg path[stroke="rgb(102, 52, 52)"]) {
    stroke: var(--bottom, #663434) !important;
}

.redeem-error {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.redeem-error .redeem-video {
    width: 120px;
    height: 120px;
}

.redeem-close {
    margin-top: 8px;
    background: var(--primary-color, #F27178);
    color: #ffffff;
    border: none;
    border-radius: 999px;
    padding: 10px 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.redeem-close:hover {
    transform: translateY(-1px);
    opacity: 0.92;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

@keyframes redeemSpin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
</style>
